/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { createContext, useContext, useState } from 'react';

import type { PageNotificationProps } from '@coursera/cds-core';
import { PageNotification, PageNotificationList } from '@coursera/cds-core';

type Notification = {
  message: string;
  type: PageNotificationProps['severity'];
};

type NotificationSystem = {
  notifications: Notification[];
  createNotification: (note: Notification) => void;
  closeNotification: (text: string) => void;
};

const NotificationContext = createContext<NotificationSystem>({} as NotificationSystem);

function useNotificationSystem(): NotificationSystem {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  return {
    notifications,
    closeNotification: (text) => {
      setNotifications(notifications.filter((note) => note.message !== text));
    },
    createNotification: (note) => {
      setNotifications((prevNotes) => [
        // Remove any duplicated messages
        ...prevNotes.filter((oldNotes) => note.message !== oldNotes.message),
        { ...note },
      ]);
    },
  };
}

function useNotification() {
  const { createNotification } = useContext(NotificationContext);

  return createNotification;
}

const styles = {
  entry: css`
    margin: var(--cds-spacing-100) 0;
  `,
};

const NotificationsSection = () => {
  const { closeNotification, notifications } = useContext(NotificationContext);
  return (
    <PageNotificationList>
      {notifications.map((note) => (
        <PageNotification
          key={note.message}
          css={styles.entry}
          severity={note.type}
          onDismiss={() => closeNotification(note.message)}
        >
          {note.message}
        </PageNotification>
      ))}
    </PageNotificationList>
  );
};

export { useNotification, NotificationContext, useNotificationSystem, NotificationsSection };
